import { type ReactNode } from "react";
import { PrivyProvider } from "@privy-io/react-auth";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SuiClientProvider, WalletProvider as SuiWalletProvider } from "@mysten/dapp-kit";
import { SuiJsonRpcClient } from "@mysten/sui/jsonRpc";
import "@mysten/dapp-kit/dist/index.css";

/**
 * Sui-side providers, wrapping the whole app so Privy (email + Solana wallets +
 * Sui embedded) and dapp-kit (external Sui wallets: Slush, Suiet) are available
 * app-wide. Inert on Solana routes — the existing Solana wallet/auth tree lives
 * inside this and is untouched.
 */

const queryClient = new QueryClient();
// @mysten/sui v2 moved the JSON-RPC client + fullnode url out of /client.
const networks = {
  testnet: new SuiJsonRpcClient({ url: "https://fullnode.testnet.sui.io:443", network: "testnet" }),
};
const PRIVY_APP_ID = import.meta.env.VITE_PRIVY_APP_ID ?? "";

export function SuiProviders({ children }: { children: ReactNode }) {
  // No App ID configured → skip Privy so the app still boots (Solana-only).
  const tree = (
    <QueryClientProvider client={queryClient}>
      <SuiClientProvider networks={networks} defaultNetwork="testnet">
        <SuiWalletProvider autoConnect>{children}</SuiWalletProvider>
      </SuiClientProvider>
    </QueryClientProvider>
  );

  if (!PRIVY_APP_ID) return tree;

  return (
    <PrivyProvider
      appId={PRIVY_APP_ID}
      config={{
        loginMethods: ["email", "wallet"],
        appearance: { theme: "light", accentColor: "#27187E", walletChainType: "solana-only" },
      }}
    >
      {tree}
    </PrivyProvider>
  );
}
